import { nanoid } from 'nanoid';

import type {
  CreateOilLogFormValues,
  OilLog,
  OilLogEntry,
  UpdateOilLogFormValues,
} from '@/store/oil-log/oil-log.dto';

import { TIME_RE, type OilEntry, type OilLogState } from './types';

// Oil logs are split per day, so an entry only captures the time of day. We
// merge the log's selected date with the entry time into a single ISO
// `date_time` for the backend, and split it back apart when loading.
const mergeDateTime = (dateIso: string, time: string): string => {
  const base = new Date(dateIso);
  if (Number.isNaN(base.getTime())) return time;
  const match = TIME_RE.exec(time.trim());
  if (!match) return base.toISOString();
  const merged = new Date(
    Date.UTC(
      base.getUTCFullYear(),
      base.getUTCMonth(),
      base.getUTCDate(),
      Number(match[1]),
      Number(match[2]),
    ),
  );
  return merged.toISOString();
};

const extractTime = (dateTimeIso: string): string => {
  const d = new Date(dateTimeIso);
  if (Number.isNaN(d.getTime())) return '';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  return `${hh}:${mm}`;
};

const entryToDto = (entry: OilEntry, date: string): OilLogEntry => ({
  dateTime: mergeDateTime(date, entry.time),
  location: entry.location,
  bunkerTakenUsg: entry.bunkerTakenUsg,
  costPerUsgAfterTaxUsd: entry.costPerUsgAfterTaxUsd,
  costUsd: entry.costUsd,
  costLocalCurrency: entry.costLocalCurrency,
  totalOnboardUsg: entry.totalOnboardUsg,
});

const entryFromDto = (entry: OilLogEntry): OilEntry => ({
  id: nanoid(),
  time: extractTime(entry.dateTime),
  location: entry.location,
  bunkerTakenUsg: entry.bunkerTakenUsg,
  costPerUsgAfterTaxUsd: entry.costPerUsgAfterTaxUsd,
  costUsd: entry.costUsd,
  costLocalCurrency: entry.costLocalCurrency,
  totalOnboardUsg: entry.totalOnboardUsg,
});

export const oilLogToFormState = (log: OilLog): OilLogState => ({
  date: log.date,
  entries: log.entries.map(entryFromDto),
});

export const formStateToCreateValues = (state: OilLogState): CreateOilLogFormValues => ({
  date: state.date,
  entries: state.entries.map((entry) => entryToDto(entry, state.date)),
});

export const formStateToUpdateValues = (
  id: string,
  state: OilLogState,
): UpdateOilLogFormValues => ({
  id,
  entries: state.entries.map((entry) => entryToDto(entry, state.date)),
});
